import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { datadogRum } from "@datadog/browser-rum";
import jwtDecode from "jwt-decode";
import { actions, useAppState } from "./AppStateContext";
import {
  MeQuery,
  useGetUserPermissionsQuery,
  useLogoutMutation,
  useMeQuery,
  useValidateSessionQuery,
} from "../graphql/generated/graphql";
import { usePostHog } from "posthog-js/react";
import Sentry from "../utils/sentry";
import { RolePermissions } from "../constants/rolesAndPermissions";
import { apolloClient } from "../graphql/graphql.client";
import { subscribeAuthEvents } from "../utils/authEvents";
import {
  getValidAccessToken,
  isTokenExpired,
  setLogoutInProgress,
  storeAccessToken,
  storeAuthTokens,
} from "../utils/authRefresh";

export enum UserRoleUI {
  STUDENT = "Student",
  FACULTY = "Faculty",
  AUDITOR = "Auditor",
  SCHOOL_ADMIN = "School Admin",
  ADMIN = "Admin",
}

export const USER_ROLE_RANK: Record<UserRoleUI, number> = {
  [UserRoleUI.STUDENT]: 0,
  [UserRoleUI.FACULTY]: 1,
  [UserRoleUI.AUDITOR]: 2,
  [UserRoleUI.SCHOOL_ADMIN]: 3,
  [UserRoleUI.ADMIN]: 4,
};

export const isBelowMinRole = (actual: UserRoleUI, required: UserRoleUI) =>
  USER_ROLE_RANK[actual] < USER_ROLE_RANK[required];

export const hasMinRole = (actual: UserRoleUI, required: UserRoleUI) =>
  USER_ROLE_RANK[actual] >= USER_ROLE_RANK[required];

export const getUserRoleFromPermissions = (permissions?: string[] | null): UserRoleUI => {
  if (!permissions) {
    return UserRoleUI.STUDENT;
  }
  if (permissions.includes(RolePermissions.wildcard)) {
    return UserRoleUI.ADMIN;
  }
  // Admin has faculty:edit permission, Faculty does not
  if (permissions.includes(RolePermissions.facultyEdit)) {
    return UserRoleUI.SCHOOL_ADMIN;
  }
  if (permissions.includes(RolePermissions.analyticsViewSchool)) {
    return UserRoleUI.AUDITOR;
  }
  // Faculty has course:edit but not faculty:edit
  if (permissions.includes(RolePermissions.courseEdit)) {
    return UserRoleUI.FACULTY;
  }
  return UserRoleUI.STUDENT;
};

interface DecodedToken {
  userId: string;
  email: string;
  name: string;
  deviceId: string;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  iat: number;
  exp: number;
}

type AuthLoginResult = {
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  userRole: UserRoleUI;
};

type AuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  didCheckToken: boolean;
};

type AuthAction = { type: "SET_LOADING"; payload: boolean } | { type: "SET_AUTH_STATE"; payload: Partial<AuthState> };

interface AuthContextType {
  meData: MeQuery | null;
  refetchMe: () => Promise<any>;
  loadingMe: boolean;
  isUpdating: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  login: (accessToken: string, refreshToken: string) => Promise<AuthLoginResult>;
  logout: () => Promise<void>;
  verifyOTPForSession: (accessToken: string) => Promise<void>;
  userInfo: {
    userId: string;
    userName: string;
    userInitial: string;
    userRole: UserRoleUI;
    userPermissions: string[];
  };
  didCheckToken: boolean;
  isAuthReady: boolean;
  isUserDataReady: boolean;
  isPermissionsReady: boolean;
}

export const decodeToken = async (token: string) => {
  const decoded = jwtDecode<DecodedToken>(token);
  return decoded;
};

export const AuthContext = createContext<AuthContextType>({
  meData: null,
  refetchMe: async () => ({}),
  loadingMe: false,
  isUpdating: false,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  login: async () => ({
    isEmailVerified: false,
    otpVerifiedForSession: false,
    userRole: UserRoleUI.STUDENT,
  }),
  logout: async () => {},
  verifyOTPForSession: async () => {},
  userInfo: {
    userId: "",
    userName: "",
    userInitial: "",
    userRole: UserRoleUI.STUDENT,
    userPermissions: [],
  },
  didCheckToken: false,
  isAuthReady: false,
  isUserDataReady: false,
  isPermissionsReady: false,
});

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_AUTH_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const initialAuthState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  didCheckToken: false,
};

export const AuthProvider = ({ children }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const explicitLogoutRef = useRef(0);

  const { dispatch: appDispatch } = useAppState();

  const posthog = usePostHog();

  const clearObservabilityUser = useCallback(() => {
    posthog?.reset(true);
    datadogRum.clearUser();
  }, [posthog]);

  const setObservabilityUserFromToken = useCallback(
    (decodedAccessToken: DecodedToken) => {
      posthog?.identify(decodedAccessToken.userId);
      datadogRum.setUser({
        id: decodedAccessToken.userId,
        name: decodedAccessToken.name,
        email: decodedAccessToken.email,
      });
    },
    [posthog]
  );

  const setObservabilityUserFromMe = useCallback((me: NonNullable<MeQuery["me"]>) => {
    datadogRum.setUser({
      id: me.id,
      name: me.name,
      email: me.email,
      isPayingUser: me.isPro,
      hasFreeTrial: !!me.trialEndDate,
    });
  }, []);

  const { data: userPermissionsData, refetch: refetchUserPermissions } = useGetUserPermissionsQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: meData,
    loading: loadingMe,
    refetch: refetchMe,
  } = useMeQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const [hasHydratedUser, setHasHydratedUser] = useState(false);
  const [hasHydratedPermissions, setHasHydratedPermissions] = useState(false);

  useEffect(() => {
    if (meData?.me) {
      setHasHydratedUser(true);
    }
  }, [meData?.me]);

  useEffect(() => {
    if (userPermissionsData?.getUserPermissions) {
      setHasHydratedPermissions(true);
    }
  }, [userPermissionsData?.getUserPermissions]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      setHasHydratedUser(false);
      setHasHydratedPermissions(false);
    }
  }, [state.isAuthenticated]);

  const userRole = useMemo(
    () => getUserRoleFromPermissions(userPermissionsData?.getUserPermissions),
    [userPermissionsData?.getUserPermissions]
  );

  const userInfo = useMemo(() => {
    const userName = meData?.me?.name || "";
    return {
      userId: meData?.me?.id || "",
      userName,
      userInitial: userName ? userName.charAt(0) : "",
      userRole: userRole,
      userPermissions: userPermissionsData?.getUserPermissions || [],
    };
  }, [meData?.me?.id, meData?.me?.name, userRole, userPermissionsData?.getUserPermissions]);

  const { data, startPolling, stopPolling } = useValidateSessionQuery({
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !state.isAuthenticated,
  });

  const [logoutMutation] = useLogoutMutation();

  const hydrateAuthState = useCallback(
    async (accessToken: string | null, refreshToken: string | null): Promise<DecodedToken | null> => {
      const hasValidAccessToken = accessToken && !isTokenExpired(accessToken, 30);
      let resolvedAccessToken = hasValidAccessToken ? accessToken : null;

      if (!resolvedAccessToken && refreshToken) {
        resolvedAccessToken = await getValidAccessToken({ forceRefresh: true });
      }

      if (resolvedAccessToken && refreshToken) {
        try {
          const decodedAccessToken = await decodeToken(resolvedAccessToken);
          setObservabilityUserFromToken(decodedAccessToken);
          dispatch({
            type: "SET_AUTH_STATE",
            payload: {
              isAuthenticated: true,
              isEmailVerified: decodedAccessToken.isEmailVerified,
              otpVerifiedForSession: decodedAccessToken.otpVerifiedForSession,
              didCheckToken: true,
            },
          });
          return decodedAccessToken;
        } catch (error) {
          Sentry.captureException(error);
          await AsyncStorage.removeItem("accessToken");
          await AsyncStorage.removeItem("refreshToken");
          clearObservabilityUser();
        }
      } else {
        clearObservabilityUser();
      }

      dispatch({
        type: "SET_AUTH_STATE",
        payload: {
          isAuthenticated: false,
          isEmailVerified: false,
          otpVerifiedForSession: false,
          didCheckToken: true,
        },
      });

      return null;
    },
    [clearObservabilityUser, setObservabilityUserFromToken]
  );

  const initializeAuth = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    if (typeof window !== "undefined") {
      const path = window.location?.pathname ?? "";
      const isAuthCallbackPath = path === "/login/lti" || path === "/login/sso";
      if (isAuthCallbackPath) {
        const params = new URLSearchParams(window.location.search);
        const hasCallbackTokens = params.has("accessToken") && params.has("refreshToken");
        if (hasCallbackTokens) {
          dispatch({
            type: "SET_AUTH_STATE",
            payload: {
              isAuthenticated: false,
              isEmailVerified: false,
              otpVerifiedForSession: false,
              didCheckToken: true,
            },
          });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
      }
    }
    const [accessToken, refreshToken] = await Promise.all([
      getValidAccessToken({ minTtlSeconds: 30 }),
      AsyncStorage.getItem("refreshToken"),
    ]);
    await hydrateAuthState(accessToken, refreshToken);
    dispatch({ type: "SET_LOADING", payload: false });
  }, [hydrateAuthState]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const checkSession = async () => {
      if (data && !data.validateSession.isValid) {
        clearObservabilityUser();
      }
    };

    checkSession();
  }, [clearObservabilityUser, data]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      stopPolling();
      return;
    }

    const pollInterval = 60000;
    startPolling(pollInterval);

    return () => {
      stopPolling();
    };
  }, [startPolling, state.isAuthenticated, stopPolling]);

  const login = useCallback(
    async (accessToken: string, refreshToken: string): Promise<AuthLoginResult> => {
      setIsUpdating(true);
      let decodedAccessToken: DecodedToken | null = null;
      let userRoleFromPermissions = UserRoleUI.STUDENT;

      try {
        await storeAuthTokens(accessToken, refreshToken);

        if (apolloClient) {
          await apolloClient.clearStore();
        }

        appDispatch({
          type: actions.RESET_STATE,
        });

        decodedAccessToken = await hydrateAuthState(accessToken, refreshToken);

        const [meResult, permissionsResult] = await Promise.all([refetchMe(), refetchUserPermissions()]);
        const me = meResult?.data?.me;

        if (me) {
          posthog?.capture("Set user properties on login.", {
            $set: {
              isPayingUser: me.isPro,
              hasFreeTrial: !!me.trialEndDate,
            },
          });
          setObservabilityUserFromMe(me);
        }

        userRoleFromPermissions = getUserRoleFromPermissions(permissionsResult?.data?.getUserPermissions);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsUpdating(false);
      }

      return {
        isEmailVerified: decodedAccessToken?.isEmailVerified ?? false,
        otpVerifiedForSession: decodedAccessToken?.otpVerifiedForSession ?? false,
        userRole: userRoleFromPermissions,
      };
    },
    [appDispatch, hydrateAuthState, posthog, refetchMe, refetchUserPermissions, setObservabilityUserFromMe]
  );

  const logout = useCallback(async () => {
    explicitLogoutRef.current += 1;
    setLogoutInProgress(true);
    setIsUpdating(true);

    dispatch({
      type: "SET_AUTH_STATE",
      payload: {
        isAuthenticated: false,
        isEmailVerified: false,
        otpVerifiedForSession: false,
        didCheckToken: true,
      },
    });

    await AsyncStorage.removeItem("accessToken");
    await AsyncStorage.removeItem("refreshToken");
    clearObservabilityUser();

    try {
      const result = await logoutMutation();

      if (!result?.data?.logout?.success) {
        throw new Error("Failed to logout");
      }

      if (apolloClient) {
        await apolloClient.clearStore();
      }

      appDispatch({
        type: actions.RESET_STATE,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsUpdating(false);
      setLogoutInProgress(false);
      explicitLogoutRef.current = Math.max(0, explicitLogoutRef.current - 1);
    }
  }, [appDispatch, clearObservabilityUser, logoutMutation]);

  const forceLogout = useCallback(
    async (_reason?: string) => {
      setIsUpdating(true);

      dispatch({
        type: "SET_AUTH_STATE",
        payload: {
          isAuthenticated: false,
          isEmailVerified: false,
          otpVerifiedForSession: false,
          didCheckToken: true,
        },
      });

      await AsyncStorage.removeItem("accessToken");
      await AsyncStorage.removeItem("refreshToken");
      clearObservabilityUser();

      try {
        if (apolloClient) {
          await apolloClient.clearStore();
        }

        appDispatch({
          type: actions.RESET_STATE,
        });
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [appDispatch, clearObservabilityUser]
  );

  useEffect(() => {
    const unsubscribe = subscribeAuthEvents((event) => {
      if (event.type === "logout") {
        if (explicitLogoutRef.current > 0) {
          return;
        }
        forceLogout(event.reason);
        return;
      }

      if (event.type === "tokensUpdated") {
        hydrateAuthState(event.accessToken, event.refreshToken);
      }
    });

    return unsubscribe;
  }, [forceLogout, hydrateAuthState]);

  const verifyOTPForSession = useCallback(
    async (accessToken: string) => {
      setIsUpdating(true);
      try {
        const storedRefreshToken = await AsyncStorage.getItem("refreshToken");
        if (storedRefreshToken) {
          await storeAuthTokens(accessToken, storedRefreshToken);
        } else {
          await storeAccessToken(accessToken);
        }
        const refreshedToken = await AsyncStorage.getItem("refreshToken");
        await hydrateAuthState(accessToken, refreshedToken);
        await refetchMe();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [hydrateAuthState, refetchMe]
  );

  const isUserDataReady = !state.isAuthenticated || hasHydratedUser;
  const isPermissionsReady = !state.isAuthenticated || hasHydratedPermissions;
  const isAuthReady =
    !state.isLoading && state.didCheckToken && (!state.isAuthenticated || (hasHydratedUser && hasHydratedPermissions));

  const value = useMemo(() => {
    return {
      meData: meData,
      refetchMe,
      loadingMe,
      userInfo,
      isUpdating,
      isAuthenticated: state.isAuthenticated,
      isEmailVerified: state.isEmailVerified,
      otpVerifiedForSession: state.otpVerifiedForSession,
      login,
      logout,
      verifyOTPForSession,
      didCheckToken: state.didCheckToken,
      isAuthReady,
      isUserDataReady,
      isPermissionsReady,
    };
  }, [
    isUpdating,
    isAuthReady,
    isPermissionsReady,
    isUserDataReady,
    loadingMe,
    login,
    logout,
    meData,
    refetchMe,
    state.didCheckToken,
    state.isAuthenticated,
    state.isEmailVerified,
    state.otpVerifiedForSession,
    userInfo,
    verifyOTPForSession,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
